import { useEffect, useMemo, useRef, useState } from "react";
import { reaction } from "mobx";
import { Box, Divider, Grid, TextField, Typography } from "@mui/material";
import { getFormValidation } from "src/Shared/Components";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import MainContentBox from "src/Shared/Components/MainContentBox/MainContentBox";
import ModalContent from "src/Shared/Components/ModalContent/ModalContent";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { fields } from "./fields";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import { ICompanyOffice } from "../../Interfaces";
import { ISitePointOfContact } from "../../Interfaces/ICompanyOffice";
import { CommonService } from "src/Shared/Services";
import { useModal, useStore } from "src/Shared/Hooks";
import CompanyOfficeService from "../../Services/CompanyOffice.service";
import { finalize } from "rxjs";
import { IResult } from "src/Shared/Interfaces";
import { ToastService } from "src/Shared/Services";
import { AxiosError } from "axios";
import { PlaceDetails } from "src/Shared/Tools/GooglePlaces";
import { ISiteRegionAutoDetect } from "src/Shared/Interfaces/ISiteRegionAutoDetect.interface";
import { resolveSiteRegion, undetectedMessage } from "./SiteRegion.rules";
import ImperansReferencePicker from "../ImperansReference/ImperansReferencePicker";
import { useImperansReferenceList } from "../ImperansReference/useImperansReferenceList";
import { CLINIC_REQUIRED_MESSAGE } from "../ImperansReference/ImperansReference.rules";
import { IImperansReferencePick } from "../../Interfaces/IImperansReference";

type Props = {
  mode: VIEW_MODE;
  companyOffice?: ICompanyOffice;
  companyRowID?: string;
  primayOfficeId?: number;
  /** Biases the address lookup to the client's own country (S3.1) and picks the Imperans clinic list. */
  countryCode?: string | null;
  /** Wizard only: the site cannot save without its Imperans servicing clinic (story 6373). */
  requireServicingClinic?: boolean;
};

const AddOrEditOffice: React.FC<Props> = ({
  mode,
  companyOffice,
  companyRowID,
  primayOfficeId,
  countryCode,
  requireServicingClinic = false,
}) => {
  const {
    rootStore: {
      companyOfficeStore: { refreshCompanyOfficeList },
      companyStore: { getCompanyMenu },
    },
  } = useStore();

  const modal = useModal();
  const [buttonText, setbuttonText] = useState<string>("Add");
  const [tittle, settittle] = useState<string>("Add Office");

  const officeForm = useMemo(() => getFormValidation(fields), []);
  const getField = (key: string) => officeForm.$(key);

  useEffect(() => {
    // officeForm.reset();
    switch (mode) {
      case VIEW_MODE.ADD:
        {
        }
        break;
      case VIEW_MODE.EDIT:
        {
          setbuttonText("Update");
          settittle("Edit Office");
        }
        break;
    }
  }, []);

  const [loading, setLoading] = useState<boolean>(false);
  const [regions, setRegions] = useState<any[]>([]);

  // The Imperans clinic that services this site (Epic 6303, story 6373): searchable, from the paired
  // Imperans's list for the client's country, never filtered by the client's franchise. Kept beside
  // the mobx form rather than in it — the form's editors have no async, retry-able picker.
  const [clinic, setClinic] = useState<IImperansReferencePick | null>(null);
  const [clinicError, setClinicError] = useState<string | null>(null);
  const clinicList = useImperansReferenceList("clinics", countryCode);

  // Points of contact are captured inline with the site but stored as ordinary contacts flagged
  // Point of Contact with access to it (S3.2), so the same person is never captured twice. One
  // blank row is always present, and empty rows are discarded on save.
  const [pointsOfContact, setPointsOfContact] = useState<ISitePointOfContact[]>([
    { fullName: "", mobile: "", emailAddress: "" },
  ]);

  useEffect(() => {
    CommonService.getRegions().subscribe({
      next: (data: any) => setRegions(data || []),
      error: () => setRegions([]),
    });
  }, []);

  // How territories map to regions, and whether detection is on for each country. Fetched once with
  // the form; null means the question has not been answered yet, which suppresses detection rather
  // than guessing at it.
  const [autoDetect, setAutoDetect] = useState<ISiteRegionAutoDetect | null>(null);
  const [detectingRegion, setDetectingRegion] = useState<boolean>(false);
  const [regionNotice, setRegionNotice] = useState<string | null>(null);

  useEffect(() => {
    CommonService.getSiteRegionAutoDetect().subscribe({
      next: (data) => setAutoDetect(data || null),
      error: () => setAutoDetect(null),
    });
  }, []);

  /**
   * The address text the structured parts in the form were derived from, or null when they are
   * empty. The parts are only true of that exact text: once it is typed over there is no place
   * behind the field any more, so they are cleared and the formatted string alone goes to Imperans
   * as authoritative (story 6356, bug 6494).
   */
  const partsAddress = useRef<string | null>(null);

  const applyAddressParts = (details: PlaceDetails | null) => {
    getField("streetAddress").set("value", details?.line1 ?? null);
    getField("suburb").set("value", details?.suburb ?? null);
    getField("city").set("value", details?.townCity ?? null);
    getField("state").set("value", details?.state ?? null);
    getField("postCode").set("value", details?.postCode ?? null);
    // The field holds the prediction's label, which is what a pick writes; Google's formatted
    // address can differ from it by a character or two, so the field's own text is what is kept.
    partsAddress.current = details ? getField("officeFullAddress").value : null;
  };

  // Typing after a pick: the text no longer matches what the parts described, so they go.
  useEffect(
    () =>
      reaction(
        () => getField("officeFullAddress").value,
        (address) => {
          // An unset field reads null or "" depending on who last set it; both are "no text".
          if (partsAddress.current !== null && `${address ?? ""}` !== partsAddress.current)
            applyAddressParts(null);
        },
      ),
    [],
  );

  /**
   * Settles the TDDA Region from where the picked address actually is (S3.4, bug 5695).
   *
   * The region used to be guessed on the server by looking for the region's name in the words of the
   * address, which could not work: "Auckland" names three franchises and no address separates them,
   * and a Google-formatted address ends in the country name, which matched every region containing
   * it. It is now the territory the coordinates fall inside, on the same KML maps the Imperans
   * client-office screen uses, so both products place a site in the same territory.
   *
   * The field stays editable throughout. A detection that fails clears it and says why, because an
   * empty Region is visibly empty whereas a wrong one is not — and the region feeds the Australian
   * confirmation-lab default.
   */
  const onAddressPicked = async (details: PlaceDetails | null) => {
    setRegionNotice(null);
    // Bug 6494: the parts used to be dropped here, so every Google-picked site reached Imperans with
    // line1, suburb, town/city and postcode empty.
    applyAddressParts(details);
    if (!details) return;

    setDetectingRegion(true);
    try {
      const outcome = await resolveSiteRegion(details, autoDetect);

      if (outcome.kind === "detected") {
        getField("franchiseID").set("value", outcome.franchiseID);
        return;
      }

      if (outcome.kind === "undetected") {
        getField("franchiseID").set("value", "");
        setRegionNotice(undetectedMessage(outcome.reason));
      }
    } finally {
      setDetectingRegion(false);
    }
  };

  const setPointOfContact = (index: number, changes: Partial<ISitePointOfContact>) => {
    setPointsOfContact((current) => {
      const next = current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...changes } : row,
      );
      // Keep exactly one blank row at the end so another contact can always be added.
      const last = next[next.length - 1];
      if (last.fullName?.trim() || last.mobile?.trim() || last.emailAddress?.trim())
        next.push({ fullName: "", mobile: "", emailAddress: "" });
      return next;
    });
  };

  const reload = () => {
    modal.close();
    // Silent refresh — the Add Comment pattern: the only progress indicator is the
    // button spinner, and the save refetches nothing but the list it changed. The
    // grid keeps the page the user was on and repaints in place.
    refreshCompanyOfficeList(companyRowID, true);

    // The sidebar badge is the site count, so only adding an office moves it —
    // an edit must not touch the shared layout at all.
    if (mode === VIEW_MODE.ADD) getCompanyMenu(companyRowID, false);
  };

  useEffect(() => {
    if (companyOffice && mode == VIEW_MODE.EDIT) {
      // The stored parts belong to the stored address text; recorded before the form is filled so
      // that filling it does not read as the user typing over a picked address (bug 6494).
      const hasStoredParts = Boolean(
        companyOffice.streetAddress ||
          companyOffice.suburb ||
          companyOffice.city ||
          companyOffice.state ||
          companyOffice.postCode,
      );
      // A legacy site may hold parts with no single string at all; "" makes any typed text count as
      // an edit so those parts are not sent against an address they never described.
      partsAddress.current = hasStoredParts ? (companyOffice.officeFullAddress ?? "") : null;
      officeForm.set(companyOffice);
      setClinic(
        companyOffice.imperansFranchiseOfficeID
          ? {
              id: companyOffice.imperansFranchiseOfficeID,
              name: companyOffice.imperansFranchiseOfficeName ?? null,
            }
          : null,
      );
      // Edit loads the stored values and overwrites them on save; Cancel changes nothing (S3.3).
      const stored = companyOffice.pointsOfContact || [];
      setPointsOfContact([
        ...stored.map((row) => ({
          fullName: row.fullName || "",
          mobile: row.mobile || "",
          emailAddress: row.emailAddress || "",
        })),
        { fullName: "", mobile: "", emailAddress: "" },
      ]);
    }
  }, [companyOffice]);

  const gridProps: IGridFormControlProps = {
    row: 2,
    col: 5,
    spacing: 1,
    itemResponsive: {
      lg: 6,
      md: 6,
      sm: 12,
    },
    inputControls: [
      {
        field: getField("officeName"),
        fieldKey: "officeName",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("subCode"),
        fieldKey: "subCode",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("division"),
        fieldKey: "division",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        // Single-string address (decision 13) with Google-backed lookup (S3.1): suggestions from
        // two characters, biased to the client's country, and free text always accepted.
        field: getField("officeFullAddress"),
        fieldKey: "officeFullAddress",
        type: EDITOR_TYPES.ADDRESS_LOOKUP,
        inputProps: { countryCode, onPlaceSelected: onAddressPicked },
      },
      {
        field: getField("sitePhone"),
        fieldKey: "sitePhone",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        // Set from the territory the picked address falls inside, and always correctable here. An
        // address outside every mapped territory, or in one no region owns yet, leaves this unset
        // with a note saying so rather than guessing (S3.4, bug 5695).
        field: getField("franchiseID"),
        fieldKey: "franchiseID",
        type: EDITOR_TYPES.DROPDOWN,
        options: regions,
        disabled: detectingRegion,
        helperText: detectingRegion
          ? "Detecting the region from the address\u2026"
          : regionNotice || undefined,
      },
      {
        field: getField("isPrimaryOffice"),
        fieldKey: "isPrimaryOffice",
        type: EDITOR_TYPES.SWITCH,
        hidden:
          mode == VIEW_MODE.ADD
            ? Boolean(primayOfficeId)
            : primayOfficeId && primayOfficeId !== companyOffice.companySiteID,
      },
      // Visit logistics are gone: parking, van, power, Wi-Fi and private room were only ever read
      // by the Imperans planning module, which most franchises bypass, so the data was captured and
      // never used. S3.6 is withdrawn (decision of 24 Aug 2026, task 5696).
    ],
    form: officeForm,
  };

  const onSubmit = () => {
    // Both rule sets mark their fields on the same click, so a blank clinic and a blank address
    // are reported together rather than one after the other.
    const clinicMissing = requireServicingClinic && !clinic?.id;
    setClinicError(clinicMissing ? CLINIC_REQUIRED_MESSAGE : null);
    if (!officeForm.isValid || clinicMissing) {
      officeForm.showErrors();
      return;
    }
    const values = officeForm.values();
    const request: ICompanyOffice = {
      ...values,
      // A text field the form never filled reads ""; Imperans takes "no part" as null, and the
      // snapshot forwards what is stored, so blanks must not reach the database (story 6356).
      streetAddress: values.streetAddress || null,
      suburb: values.suburb || null,
      city: values.city || null,
      state: values.state || null,
      postCode: values.postCode || null,
      imperansFranchiseOfficeID: clinic?.id ?? null,
      imperansFranchiseOfficeName: clinic?.name ?? null,
      // Rows with nothing in them are discarded on save (S3.1).
      pointsOfContact: pointsOfContact.filter(
        (row) => row.fullName?.trim() || row.mobile?.trim() || row.emailAddress?.trim(),
      ),
    };
    setLoading(true);
    switch (mode) {
      case VIEW_MODE.ADD:
        request.companyRowID = companyRowID;
        var companyOffice: ICompanyOffice[] = [request];
        CompanyOfficeService.addCompanySite(companyOffice)
          .pipe(
            // No app-level loader: it renders the header Backdrop over the whole
            // viewport, which is what made a save look like a page reload. The
            // button spinner reports progress instead (Add Comment pattern).
            finalize(() => {
              setLoading(false);
            }),
          )
          .subscribe({
            next: (data: IResult<string>) => {
              ToastService.success(data.message);
              reload();
              // modal.close();
            },
            error: (error: AxiosError) => ToastService.axiosError(error),
          });
        break;

      case VIEW_MODE.EDIT:
        CompanyOfficeService.updateOffice(request)
          .pipe(
            finalize(() => {
              setLoading(false);
            }),
          )
          .subscribe({
            next: (data: IResult<string>) => {
              ToastService.success(data.message);
              reload();
              // modal.close();
            },
            error: (error: AxiosError) => ToastService.axiosError(error),
          });
    }
  };

  return (
    <>
      <ModalContent
        onSaveButtonClick={onSubmit}
        buttonText={buttonText}
        title={tittle}
        btnLoading={loading}
      >
        <GridFormControl {...gridProps} />

        <Box sx={{ mt: 1 }}>
          <ImperansReferencePicker
            label="Servicing clinic"
            list={clinicList}
            hasCountry={Boolean(countryCode)}
            value={clinic}
            onChange={(pick) => {
              setClinic(pick);
              if (pick?.id) setClinicError(null);
            }}
            required={requireServicingClinic}
            error={Boolean(clinicError)}
            helperText={
              clinicError ||
              "The Imperans clinic that will service this site — any clinic in the country, most used first."
            }
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600}>
          Points of contact
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Saved as contacts flagged Point of Contact with access to this site, so nobody is entered
          twice. A point of contact does not receive results unless they are also given results
          access.
        </Typography>
        {pointsOfContact.map((row, index) => (
          <Grid container spacing={1} key={index} sx={{ mb: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={row.fullName || ""}
                onChange={(e) => setPointOfContact(index, { fullName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Mobile"
                value={row.mobile || ""}
                onChange={(e) => setPointOfContact(index, { mobile: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                value={row.emailAddress || ""}
                onChange={(e) => setPointOfContact(index, { emailAddress: e.target.value })}
              />
            </Grid>
          </Grid>
        ))}
      </ModalContent>
    </>
  );
};

export default AddOrEditOffice;
